import React, { useCallback, useEffect, useState, useRef } from "react";
import { Spinner } from "react-bootstrap";
import HealthAreaSection, { getStatusSexualHealth } from "./HealthAreaSection";
import "../../../assets/scss/_blood-report-page.scss";
import drIcon from "../../../assets/icons/Doctor.svg";
import { DoctorLetterModal } from "./DoctorLetterModal";
import { iconHelper,formatDateToOrdinal } from "../../../utils/utils";
import DoctorLetter from "./DoctorLetter";
import HorizontalSection from "./HorizontalSection";
import useApis from "../../../services/useAPI";
import ReportDownloadSection from "../../WellnessReports/ReportDownloadSection";
import ResultCard from "../../../stories/cards/ResultCard";
import { useParams } from "react-router-dom";
import { bloodReportDownload } from "../../../utils/constants";
import PillTabs from "../../../stories/Tabs/PillTabs";
import InsightsSection from "./InsightsSection";
import { NewMobileHealthArea } from "./NewMobileHealthArea";
import { MobileHealthArea } from "./MobileHealthArea";
import { ErrorDisplay } from "../../Errors/ErrorDisplay";
import { useMediaQuery } from "react-responsive";


/**
 * Gets the status of the health area.
 */
function getAreaStatus(health_area) {
  const areaName = String(health_area?.area ?? "");
  // If the area is not "sexual health"
  if (areaName.toLowerCase() !== "sexual health") {
    return (health_area?.health_areas || []).map((area) => area.area_status);
  }
  // For "sexual health," process markers
  return getStatusSexualHealth(health_area?.markers || []);
}

const DoctorNotesSection = "doctor_notes";

/**
 * BloodReportPage component
 * This component displays a detailed blood report, including sections for
 * health areas and doctor's notes. It manages loading states and handles
 * the display of the doctor's letter in a modal.
 */
function BloodReportPage() {
  const params = useParams();
  const { test_kit_id } = params;
  const [report, setReport] = useState(null);
  const [reportCollectedDate, setReportCollectedDate] = useState("");
  const [reportedDate, setReportedDate] = useState("");
  const [vitaminB12Value, setVitaminB12Value] = useState(null);
  const [updatedHealthAreas, setUpdatedHealthAreas] = useState(null);
  const [currentSection, setCurrentSection] = useState(DoctorNotesSection);
  const [letterModalVisible, setLetterModalVisible] = useState(false);
  const [hasPartialMarker, setHasPartialMarker] = useState(false);
  const [doctorImgUrl, setDoctorImgUrl] = useState(null);
  const getApiUrl = `/api/dashboard/report/${test_kit_id}`;
  const [error, setError] = useState("");
  const { callApi, resultGet, serverError } = useApis();
  const isMobile = useMediaQuery({ maxWidth: 700 });

  const openMarkerRef = useRef(null);
  const handleSeeDetails = (markerCode) => { openMarkerRef.current?.(markerCode); };


  useEffect(() => {
    if (resultGet?.profile) {
      setDoctorImgUrl(`data:image/png;base64,${resultGet.profile}`);
    } else {
      setDoctorImgUrl(null);
    }
  }, [resultGet?.profile]);

  useEffect(() => {
    setError(serverError);
  }, [serverError]);

  const getPartialHealthAreas = useCallback(
    (partialResult) => {
      const healthAreaNames = (partialResult || []).map((item) => item.name);

      const bioMarkerNames = (partialResult || [])
        .flatMap((item) =>
          item.name === "Full blood count"
            ? []
            : (item.area?.markers ?? [])
                .filter((marker) => marker.partial === "true")
                .map((marker) => marker.name)
        )
        .filter(Boolean);

      if (healthAreaNames.length > 1) {
        return `${healthAreaNames[0]} & ${
          healthAreaNames[1] === "Glucose"
            ? bioMarkerNames[0]
            : healthAreaNames[1]
        }`;
      }

      return healthAreaNames[0] === "Glucose"
        ? bioMarkerNames[0]
        : healthAreaNames[0];
    },
    [resultGet]
  );

  useEffect(() => {
    callApi({}, getApiUrl, "GET");
  }, []);

  useEffect(() => {
    const markers = (resultGet?.area_partial_markers_only ?? [])
      .flatMap((item) => item?.partial_markers ?? [])
      .filter((marker) => marker.partial === "true");
    setHasPartialMarker(markers);

    if (resultGet?.folate_flag && resultGet?.is_retest_kit) {
      let reportDeepCopy = structuredClone(resultGet?.data);

      // FIXED: removed [0], safe optional chaining
      let updatedVitaminMarkers = (reportDeepCopy?.health_areas || [])
        .find((area) => area?.area === "Vitamins & Minerals")
        ?.markers?.find((marker) => marker?.name === "Folate");

      let updatedVitaminMarkersSH = (reportDeepCopy?.health_areas || [])
        .find((area) => area?.area === "Sexual Health")
        ?.markers?.filter(
          (marker) => marker?.name === "HIV" || marker?.name === "Syphilis"
        );
      // Build updated health areas
      reportDeepCopy.health_areas = (resultGet?.data?.health_areas || []).map(
        (areaObj) => {
          if (["Vitamins & Minerals", "Sexual Health"].includes(areaObj.area)) {
            const originalMarkers =
              (resultGet?.original_kit_report_data?.health_areas || []).find(
                (area) => area?.area === areaObj.area
              )?.markers || [];

            return {
              ...areaObj,
              markers: [
                ...(areaObj.area === "Vitamins & Minerals"
                  ? [updatedVitaminMarkers]
                  : [...updatedVitaminMarkersSH]
                ).filter(Boolean),
                ...originalMarkers.filter(
                  (marker) =>
                    marker?.name !== "Folate" &&
                    marker?.name !== "HIV" &&
                    marker?.name !== "Syphilis"
                ),
              ],
            };
          }
          return areaObj;
        }
      );
      setReport(reportDeepCopy);
    } else {
      setReport(resultGet?.data);
      setReportCollectedDate(resultGet?.date_collection);
      setReportedDate(resultGet?.date_reported);

      // FIXED: safe indexing
      setVitaminB12Value(
        resultGet?.data?.health_areas?.[0]?.markers?.[0]?.value ?? null
      );
    }

    // Add partial marker flag
    const updatedHealthAreasLocal = (resultGet?.data?.health_areas ?? []).map(
      (area) => {
        const hasPartialMarker = (area?.markers ?? []).some(
          (marker) => marker.partial === "true"
        );
        return {
          ...area,
          partialMarker: hasPartialMarker,
        };
      }
    );

    setUpdatedHealthAreas(updatedHealthAreasLocal);
  }, [resultGet]);

  if (error) {
    return <ErrorDisplay error={error} consent={false} />;
  }

  if (!report) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  /**
   * Toggles the visibility of the doctor's letter modal.
   */
  const showDrLetter = () => {
    setCurrentSection(DoctorNotesSection);
    const isSmallScreen = window.innerWidth < 700;

    if (isSmallScreen) {
      setLetterModalVisible(!letterModalVisible);
    }
  };

  const renderSingleHealthArea = (report) => (
    <HealthAreaSection
      area_status={report?.area_status}
      {...(report?.health_areas?.[0] ?? {})}
      area={report?.title}
      type={report?.report_type}
      isPartialResult={() => getPartialHealthAreas(isPartialResult)}
      retestKit={resultGet?.is_retest_kit}
      folate_flag={resultGet?.folate_flag}
      original_kit_review_ts={resultGet?.original_kit_review_ts}
      retest_review_ts={resultGet?.retest_review_ts}
      area_partial_markers_only={resultGet?.area_partial_markers_only}
      isPartialArea={report?.health_areas?.[0]?.partialMarker}
      report_type={report?.report_type}
      onSeeDetails={handleSeeDetails}
    />
  );

  const renderMultipleHealthAreas = (area, processedAreaStatus, reportType) => (
    <HealthAreaSection
      area_status={processedAreaStatus}
      area={area?.area}
      {...area}
      type={reportType}
      isPartialResult={() => getPartialHealthAreas(isPartialResult)}
      retestKit={resultGet?.is_retest_kit}
      folate_flag={resultGet?.folate_flag}
      original_kit_review_ts={resultGet?.original_kit_review_ts}
      retest_review_ts={resultGet?.retest_review_ts}
      area_partial_markers_only={resultGet?.area_partial_markers_only}
      isPartialArea={area?.partialMarker}
      report_type={report?.report_type}
      isRejected={area?.is_rejected}
      partialResult={resultGet?.partial_result}
      onSeeDetails={handleSeeDetails}
    />
  );

  const renderDoctorLetter = () => (
    <DoctorLetter
      doctor={report?.assigned_doctor}
      patient={report?.customer}
      overallStatus={report?.overall_status}
      testName={report?.title}
      content={report?.doctors_letter || ""}
      missingAssessment={!report?.assessment_id}
      isPartialResult={hasPartialMarker}
      isPartialReport={resultGet?.partial_result}
    />
  );

  const renderInsightsSection = () => (
    <InsightsSection
      dateReported={reportedDate}
      vitaminB12Value={resultGet?.data?.health_areas[0]?.markers[0]?.value}
      // doctor={report?.assigned_doctor}
      // patient={report?.customer}
      // overallStatus={report?.overall_status}
      // testName={report?.title}
      // content={report?.doctors_letter || ""}
      // missingAssessment={!report?.assessment_id}
      // isPartialResult={hasPartialMarker}
    />
  );

  const insights = {
    name: "InsightsSection", // use a string name
    label: `Insights`,
    dateReported: reportedDate,
    vitaminB12Value: vitaminB12Value,
    icon: "",
    background: "#F9EFE2",
    content: renderInsightsSection,
  };
  let navItems = [];
  navItems.push({
    name: DoctorNotesSection,
    label: `Doctor's letter`,
    icon: drIcon,
    background: "#F9EFE2",
    content: renderDoctorLetter,
  });

  if ((report?.health_areas ?? []).length === 1) {
    navItems.push({
      area: { ...(report?.health_areas?.[0] ?? {}), area: "Your results" },
      name: `${report?.report_type}`,
      label: `${report?.title}`,
      markers: report?.health_areas?.[0]?.markers ?? [], 
      status: getAreaStatus(report),
      icon: iconHelper(`Biomarker ${report?.title}`),
      content: () => renderSingleHealthArea(report),
    });
  } else {
    (updatedHealthAreas ?? []).forEach((area, index) => {
      const processedAreaStatus = report?.health_areas?.[index]?.area_status;
      navItems.push({
        area: area,
        area_status: processedAreaStatus,
        name: area.area,
        label: area.area,
        markers: area.markers ?? [],
        status: getAreaStatus(report),
        icon: iconHelper(`Biomarker ${area.area}`),
        content: () =>
          renderMultipleHealthAreas(
            area,
            processedAreaStatus,
            report.report_type
          ),
      });
    });

    // Stable sort without triggering unsafe optional chain access
    let nonDoc = navItems.slice(1);
    nonDoc.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    navItems = [navItems[0], ...nonDoc];
  }
  const mobileItems = navItems.filter(
    (item) => item.name !== DoctorNotesSection
  );
  if (
    report?.report_type == "boots-blood-vitamin-b12" ||
    report?.report_type === "blood-vitamin-b12"
  ) {
    navItems.push(insights);
  }

  const isPartialResult = navItems.filter(
    (item) =>
      !item?.area_status &&
      item?.name !== DoctorNotesSection &&
      ["Glucose", "Full blood count"].includes(item?.name ?? "")
  );
  return (
    <div className={`row justify-content-center ${isMobile ? "" : "mt-3 pt-2"} `}>
      <div className="col-md-12">
        <section className="blood-report-container mb-4">
          <header className="blood-repojrt-header">
            <ResultCard
              report_link={"location.pathname"}
              report={report}
              test_name={report?.title}
              dateCollected={reportCollectedDate}
              dateReported={reportedDate}
              test_type={report?.type}
              downloadButton={
                <ReportDownloadSection
                  textDownloadButton={false}
                  className="custom-download-pdf"
                  url={`${bloodReportDownload}${
                    report?.test_kit_id
                  }/${JSON.parse(localStorage.getItem("user_id"))}/`}
                />
              }
              resultDetails={true}
              showCard={false}
              doctor_name={report?.assigned_doctor}
            />
          </header>
          <div
            className={
              "blood-report-pills-tab"
            }
            style={{ width: "100%" }}
          >
            <PillTabs
              items={navItems}
              current={currentSection}
              setCurrentItem={setCurrentSection}
            />
          </div>
  
          <div className="blood-report-main--desktop mt-4 mb-4">
            {(currentSection !== "doctor_notes" || !isMobile) && (
              <HorizontalSection
                items={navItems}
                setCurrentItem={setCurrentSection}
                current={currentSection}
                retestKit={resultGet?.is_retest_kit || resultGet?.folate_flag}
                isPartialResult={() => getPartialHealthAreas(isPartialResult)}
                doctor={report?.assigned_doctor}
                drImg={doctorImgUrl}
                onRegisterOpener={(fn) => { openMarkerRef.current = fn; }}
                reportedDate={formatDateToOrdinal(resultGet?.date_reported)}
              />
            )}
          </div>

          <div className="blood-report-main--mobile">
            {isMobile && currentSection === "doctor_notes" && (
              <NewMobileHealthArea
                current={currentSection}
                items={navItems}
                doctor={report?.assigned_doctor}
                drImg={doctorImgUrl}
              />
            )}
          </div>
           {/* {mobileItems?.length > 1 &&
              currentSection !== "doctor_notes" &&
              (
                 <HorizontalSection
                  items={navItems}
                  setCurrentItem={setCurrentSection}
                  current={currentSection}
                  retestKit={resultGet?.is_retest_kit || resultGet?.folate_flag}
                  isPartialResult={() => getPartialHealthAreas(isPartialResult)}
                  doctor={report?.assigned_doctor}
                  drImg={doctorImgUrl}
                  onRegisterOpener={(fn) => { openMarkerRef.current = fn; }}
                  reportedDate={formatDateToOrdinal(resultGet?.date_reported)}
                />
              )}
          </div> */}
          {/* Doctor letter mobile modal */}
          <DoctorLetterModal
            title={`Letter from doctor`}
            isOpen={!!letterModalVisible}
            toggle={showDrLetter}
          >
            {renderDoctorLetter()}
          </DoctorLetterModal>
        </section>
      </div>
    </div>
  );
}

export default BloodReportPage;
